import { useTranslation } from 'react-i18next';
import { WindowUsage } from 'features/usage/services/usage-service';
import { formatPercent, formatWindowName } from 'features/usage/utils/usage-format';
import { cn } from 'lib/utils';
import { UsageBar } from './usage-bar';

/** "15:00", "tomorrow 00:00" or "Oct 3 00:00", on the viewer's clock. */
export function resetTime(iso: string, tomorrowLabel: string) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (at.toDateString() === today.toDateString()) return time;
  if (at.toDateString() === tomorrow.toDateString()) return `${tomorrowLabel} ${time}`;
  return `${at.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

function WindowLimitRow({ window, calm }: { window: WindowUsage; calm: boolean }) {
  const { t } = useTranslation();
  const full = window.percent >= 100;
  const isWarning = full && !calm;
  const when = window.resets_at
    ? resetTime(window.resets_at, t('TOMORROW', { defaultValue: 'tomorrow' }))
    : '';

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-foreground">{formatWindowName(window.name, t)}</span>
        <span className={cn('font-mono', isWarning ? 'text-warning-high-emphasis' : 'text-muted-foreground')}>
          {full
            ? t('WINDOW_LIMIT_REACHED', { defaultValue: 'Limit reached' })
            : formatPercent(window.percent, 2)}
        </span>
      </div>
      <UsageBar percent={window.percent} calm={calm} />
      {when && (
        <div className="text-xs text-muted-foreground">
          {t('WINDOW_RESETS', { when, defaultValue: 'Resets {{when}}' })}
        </div>
      )}
    </div>
  );
}

/** The limit windows that hold for this payer, shown under the month's credits. */
export function UsageWindowLimits({
  windows,
  calm = false,
}: {
  windows: WindowUsage[];
  /** Top-up credits cover a full window, so it is shown without the warning look. */
  calm?: boolean;
}) {
  const { t } = useTranslation();

  if (!windows.length) return null;

  return (
    <div className="space-y-3 border-t border-border/70 pt-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('WINDOW_LIMITS', { defaultValue: 'Usage limits' })}
      </div>
      {windows.map((window) => (
        <WindowLimitRow key={window.key} window={window} calm={calm} />
      ))}
    </div>
  );
}
